import { ASSET_CLASS_IDS, TOKENIZATION_TYPE_IDS } from "~/consts";
import {
    ApiQuery,
    ApiQueryEndpoints,
    Filter,
    FilterOperators,
} from "~/modules/filters/types";
import { mergeApiQueries } from "~/modules/filters/mergeApiQueries";
import { TokenTimeseriesColumns } from "~/modules/token_timeseries/types";
import { DistributionType, DistributionTypes } from "~/types";
import { AssetTimeseriesColumns } from "~/modules/asset_timeseries_v4/types";

// // // //

const EXCLUDE_STABLECOINS_FILTER: Filter = {
    operator: FilterOperators.NotEquals,
    field: TokenTimeseriesColumns.assetClassID,
    value: ASSET_CLASS_IDS.Stablecoin,
};

// // // //

/**
 * Applies distribution type and stablecoin filters to an existing query.
 * Used by VisualizationModeCard to layer distribution controls on top of mode queries.
 */
export function applyDistributionFilters(props: {
    query: ApiQuery;
    selectedPlatformType: DistributionType;
    includeStablecoins: boolean;
    endpoint:
        | ApiQueryEndpoints.AssetTimeseriesV4
        | ApiQueryEndpoints.TokenTimeseriesV4
        | ApiQueryEndpoints.FundFlowsTimeseriesV4
        | null;
}): ApiQuery {
    const { query, selectedPlatformType, includeStablecoins, endpoint } = props;

    if (!endpoint) {
        return query;
    }

    const tokenizationTypeFilterField =
        endpoint === ApiQueryEndpoints.AssetTimeseriesV4
            ? AssetTimeseriesColumns.tokenizationTypeIDs
            : TokenTimeseriesColumns.tokenizationTypeID;

    const tokenizationTypeID =
        selectedPlatformType === DistributionTypes.Distributed
            ? TOKENIZATION_TYPE_IDS.OnChainDistributed
            : TOKENIZATION_TYPE_IDS.OnChainRepresented;

    const tokenizationTypeFilterValue =
        endpoint === ApiQueryEndpoints.AssetTimeseriesV4
            ? [tokenizationTypeID]
            : tokenizationTypeID;

    const tokenizationTypeFilterOperator =
        endpoint === ApiQueryEndpoints.AssetTimeseriesV4
            ? FilterOperators.Includes
            : FilterOperators.Equals;

    // Layer on platform type filter
    const withPlatformType =
        selectedPlatformType !== "all"
            ? mergeApiQueries({
                  baseQuery: query,
                  query: {
                      filter: {
                          field: tokenizationTypeFilterField,
                          operator: tokenizationTypeFilterOperator,
                          value: tokenizationTypeFilterValue,
                      },
                  },
              })
            : query;

    // Exclude stablecoins when toggled off or when viewing "represented" platform type
    const shouldExcludeStablecoins =
        !includeStablecoins ||
        selectedPlatformType === DistributionTypes.Represented;

    if (shouldExcludeStablecoins) {
        return mergeApiQueries({
            baseQuery: withPlatformType,
            query: { filter: EXCLUDE_STABLECOINS_FILTER },
        });
    }

    return withPlatformType;
}
